import type { Request } from '@/api'
import { ApiError } from '@/handleApiError'
import { createEmptyAddress } from '@/modules/address/helpers'
import { OrdersView, PreparedOrder } from '@/modules/order/types'
import useOrders from '@/modules/order/useOrders'
import useOwnUser from '@/modules/user/useOwnUser'
import { t } from '@/plugins/i18n.ts'
import { ViewToggle } from '@/types'
import {
  ApprovalRequest,
  Checkout,
  ContactData,
  FilterIdentifier,
  OrderingConditions,
  OrderPostDto,
  ProposeOrderPostDto
} from '@/types/index'
import {
  PortalOrderStatus,
  type PortalOrderStatus as PortalOrderStatusType
} from '@/types/types.gen'
import { mdiNoteCheck, mdiViewDashboard, mdiViewList } from '@mdi/js'

const applyFilters = (config: Request, filters: FilterIdentifier[]): Request => {
  if (Array.isArray(filters) && filters.length) {
    const allSelectedValues = filters.flatMap((identifier) => identifier.values)

    const archived =
      allSelectedValues.filter((value) => value.term.startsWith('ARCARCHIVED')).length > 0
    const notarchived =
      allSelectedValues.filter((value) => value.term.startsWith('ARCNOTARCHIVED')).length > 0
    if (archived != notarchived) {
      config.params.archived = archived
    }

    const statuses = allSelectedValues.filter((value) => value.term.startsWith('DST'))
    if (statuses.length > 0) {
      config.params.status = statuses.map((value) => value.term.substring(3)).join(',')
    }

    const creators = allSelectedValues.filter((value) => value.term.startsWith('EMP'))
    if (creators.length > 0) {
      config.params.creators = creators.map((value) => value.term).join(',')
    }
  }

  return config
}

const arrivedStatuses: PortalOrderStatusType[] = [
  PortalOrderStatus.PREPARING,
  PortalOrderStatus.ARRIVED,
  PortalOrderStatus.ARRIVED_WARNING,
  PortalOrderStatus.BLOCKED_BYLIMIT,
  PortalOrderStatus.PAYMENT_AWAITING,
  PortalOrderStatus.IN_TOPI_CHECKOUT,
  PortalOrderStatus.PENDING_TOPI_APPROVAL
]

const errorStatuses: PortalOrderStatusType[] = [
  PortalOrderStatus.ERROR,
  PortalOrderStatus.REJECTED,
  PortalOrderStatus.CANCELED,
  PortalOrderStatus.SENDING_ERROR,
  PortalOrderStatus.PORTAL_APPROVAL_DENIED,
  PortalOrderStatus.BLOCKED,
  PortalOrderStatus.TOPI_DECLINED,
  PortalOrderStatus.TOPI_EXPIRED
]

const deletableStatuses: PortalOrderStatusType[] = [
  PortalOrderStatus.PREPARING,
  PortalOrderStatus.ORDERED_ELSEWHERE
]

const getRelatedStatuses = (status: PortalOrderStatusType): PortalOrderStatusType[] => {
  switch (status) {
    case PortalOrderStatus.ARRIVED:
      return arrivedStatuses
    case PortalOrderStatus.ERROR:
      return errorStatuses
    default:
      return [status]
  }
}

const isErrorStatus = (status: PortalOrderStatusType): boolean => {
  return errorStatuses.includes(status)
}

const getPortalOrderStatusColor = (status: PortalOrderStatusType): string => {
  switch (status) {
    case PortalOrderStatus.ARRIVED_WARNING:
    case PortalOrderStatus.BLOCKED_BYLIMIT:
    case PortalOrderStatus.PAYMENT_AWAITING:
    case PortalOrderStatus.IN_TOPI_CHECKOUT:
    case PortalOrderStatus.PENDING_TOPI_APPROVAL:
      return 'orange-darken-2'
    case PortalOrderStatus.ERROR:
    case PortalOrderStatus.REJECTED:
    case PortalOrderStatus.PORTAL_APPROVAL_DENIED:
    case PortalOrderStatus.SENDING_ERROR:
    case PortalOrderStatus.CANCELED:
    case PortalOrderStatus.BLOCKED:
    case PortalOrderStatus.TOPI_EXPIRED:
    case PortalOrderStatus.TOPI_DECLINED:
      return 'red'
    default:
      return 'green-darken-2'
  }
}

const formatDistributors = (suppliers: string[]): string => {
  let title = ''
  suppliers.forEach((supplier, key) => {
    title += supplier
    if (key !== suppliers.length - 1) {
      title += ', '
    }
  })

  return title
}

const getDefaultBillingContactData = (): ContactData => {
  const ownUser = useOwnUser().ownUser
  return {
    firstName: ownUser.firstName,
    lastName: ownUser.lastName,
    email: ownUser.email,
    phone: ownUser.phone,
    phoneMobile: ownUser.phoneMobile
  }
}

const createPreparedOrder = (
  checkout: Checkout,
  orderingConditions: OrderingConditions
): PreparedOrder => {
  return {
    orderNumber: checkout.orderNumber ?? undefined,
    billingAddress: checkout.billingAddress.default ?? createEmptyAddress(),
    billingContactData: getDefaultBillingContactData(),
    deliveryAddress: checkout.deliveryAddress.default ?? createEmptyAddress(),
    deliveryContactData: undefined,
    usePartialShipment: orderingConditions.isPartialShipmentEnabled
  }
}

const createOrderPostDto = (preparedOrder: PreparedOrder, timestamp: string): OrderPostDto => {
  return { timestamp, ...preparedOrder }
}

const createProposedOrderPostDto = (
  preparedOrder: PreparedOrder,
  timestamp: string,
  commentToApprovers: string
): ProposeOrderPostDto => {
  return { timestamp, commentToApprovers, ...preparedOrder }
}

/**
 * From a business perspective, a timeout is not a normal error but an "unknown state":
 * the request may already have been successfully processed on the server side.
 * Detects both client-side timeouts (ECONNABORTED → code 408) as well as
 * server-side gateway timeouts (504).
 */
const isTimeoutError = (error: ApiError): boolean => {
  return error.code === 408 || error.code === 504
}

/**
 * Checks whether a 409 error indicates that an equivalent order already exists
 * (e.g., due to a double-click after a timeout), rather than the cart being outdated.
 */
const isOrderAlreadyCreatedError = (error: ApiError): boolean => {
  const detail = error.axiosError?.response?.data?.detail?.toLowerCase() ?? ''
  return detail.includes('order already created')
}

/**
 * Checks whether a 403 error indicates that the quote has already been ordered.
 * The backend returns this when attempting to order a quote that is no longer in an orderable stage.
 */
const isQuoteAlreadyOrderedError = (error: ApiError): boolean => {
  const detail = error.axiosError?.response?.data?.detail?.toLowerCase() ?? ''
  return error.code === 403 && detail.includes('ordering is not allowed')
}

const getOrdersViews = (): ViewToggle<OrdersView>[] => [
  {
    name: OrdersView.LIST,
    altText: t('viewToggle.ordersList'),
    icon: mdiViewList,
    restriction: () => true,
    showOnMobile: true
  },
  {
    name: OrdersView.GRID,
    altText: t('viewToggle.ordersGrid'),
    icon: mdiViewDashboard,
    restriction: () => true,
    showOnMobile: false
  },
  {
    name: OrdersView.APPROVALS,
    altText: t('viewToggle.ordersApprovals'),
    icon: mdiNoteCheck,
    label: t('orderApproval.approvableOrders'),
    restriction: () => useOrders().newApprovableOrders,
    showOnMobile: true,
    count: {
      loadFunction: useOrders().checkApprovableOrders,
      getter: useOrders().hasApprovableOrders
    }
  }
]

const isOrderApprovableByMe = (approvalRequest: ApprovalRequest): boolean => {
  const ownUser = useOwnUser().ownUser

  return approvalRequest.assignees.find((assignee) => assignee.id === ownUser.id) != undefined
}

export {
  applyFilters,
  arrivedStatuses,
  createOrderPostDto,
  createPreparedOrder,
  createProposedOrderPostDto,
  deletableStatuses,
  errorStatuses,
  formatDistributors,
  getOrdersViews,
  getPortalOrderStatusColor,
  getRelatedStatuses,
  isErrorStatus,
  isOrderAlreadyCreatedError,
  isOrderApprovableByMe,
  isQuoteAlreadyOrderedError,
  isTimeoutError
}
